import type { RealtimeIngressDiagnosticReason, RealtimeTelemetryOverview } from '@callhub/shared';

export type RealtimeTelemetryAlertSeverity = 'watch' | 'critical';

export type RealtimeTelemetryAlert = {
  id:
    | 'sample_size'
    | 'fallback_rate'
    | 'empty_transcripts'
    | 'attempt_failures'
    | 'stream_attempt_failures'
    | 'delayed_chunk_gaps'
    | 'stream_empty_transcripts'
    | 'worsening_trend'
    | 'stale_unfinalized_streams'
    | 'stream_finalize';
  severity: RealtimeTelemetryAlertSeverity;
  title: string;
  detail: string;
  currentValue: string;
  thresholdGuidance: string;
  runbook: string;
  runbookHref: '#realtime-telemetry-playbook';
  runbookLabel: 'Open transcript runbook';
};

export type RealtimeIngressRecoveryReasonSummary = {
  reason: RealtimeIngressDiagnosticReason;
  label: string;
  count: number;
  latestAt: string | null;
};

const REALTIME_INGRESS_REASON_LABELS: Record<RealtimeIngressDiagnosticReason, string> = {
  invalid_stream_token: 'Invalid stream token',
  unauthorized_before_start: 'Frame received before start authorization',
  missing_stream_sid: 'Missing stream SID',
  mismatched_stream_sid: 'Mismatched stream SID',
  missing_call_record: 'Missing call record',
  call_record_conversation_mismatch: 'Call record / conversation mismatch',
  missing_team_scoped_conversation: 'Missing team-scoped conversation',
};

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function buildRealtimeIngressRecoveryReasons(
  telemetry: RealtimeTelemetryOverview,
): RealtimeIngressRecoveryReasonSummary[] {
  const { counts, latestByReason } = telemetry.ingressDiagnostics;

  return (Object.keys(counts) as RealtimeIngressDiagnosticReason[])
    .filter((reason) => counts[reason] > 0)
    .map((reason) => ({
      reason,
      label: REALTIME_INGRESS_REASON_LABELS[reason],
      count: counts[reason],
      latestAt: latestByReason[reason],
    }))
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }

      const leftTime = left.latestAt ? Date.parse(left.latestAt) : 0;
      const rightTime = right.latestAt ? Date.parse(right.latestAt) : 0;
      if (rightTime !== leftTime) {
        return rightTime - leftTime;
      }

      return left.label.localeCompare(right.label);
    });
}

export function buildRealtimeIngressRecoveryActions(
  telemetry: RealtimeTelemetryOverview,
): string[] {
  const { counts } = telemetry.ingressDiagnostics;
  const actions: string[] = [];

  if (
    counts.invalid_stream_token > 0 ||
    counts.unauthorized_before_start > 0 ||
    counts.missing_stream_sid > 0 ||
    counts.mismatched_stream_sid > 0
  ) {
    actions.push(
      'Confirm the TwiML stream token and streamSid continuity before retrying transcript collection.',
    );
  }

  if (
    counts.missing_call_record > 0 ||
    counts.call_record_conversation_mismatch > 0 ||
    counts.missing_team_scoped_conversation > 0
  ) {
    actions.push(
      'Inspect the call record and team-scoped conversation linkage before reconnecting the realtime stream.',
    );
  }

  actions.push(
    'Use the non-auth ingress runbook when webhook failures cannot be mapped to a team safely.',
  );

  if (actions.length < 3) {
    actions.push(
      'Check the most recent incident list before replaying or restarting any live stream.',
    );
  }

  return actions.slice(0, 3);
}

export function buildRealtimeTelemetryAlerts(
  telemetry: RealtimeTelemetryOverview,
): RealtimeTelemetryAlert[] {
  const alerts: RealtimeTelemetryAlert[] = [];
  const chunkCount = telemetry.totalTranscriptChunks;

  if (chunkCount < 12) {
    alerts.push({
      id: 'sample_size',
      severity: 'watch',
      title: 'Low transcript sample size',
      detail:
        'Realtime transcript telemetry currently has limited chunk volume for reliable quality interpretation.',
      currentValue: `${telemetry.totalTranscriptChunks} transcript chunks observed in the selected window.`,
      thresholdGuidance:
        'Keep alert thresholds unchanged until at least 12 transcript chunks or a longer lookback window confirms the same pattern.',
      runbook:
        'Increase lookback window or collect at least 12 transcript chunks before tuning models.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  if (chunkCount >= 12 && telemetry.fallbackRate >= 0.45) {
    alerts.push({
      id: 'fallback_rate',
      severity: 'critical',
      title: 'High fallback usage',
      detail: 'A large share of transcript chunks required fallback model attempts in this window.',
      currentValue: `${formatPercent(telemetry.fallbackRate)} fallback usage across ${chunkCount} transcript chunks.`,
      thresholdGuidance:
        'Critical at >=45% fallback usage with at least 12 chunks. Review transcript model routing before loosening alert thresholds.',
      runbook:
        'Investigate primary transcript model health and evaluate switching default transcript model for this team.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  } else if (chunkCount >= 12 && telemetry.fallbackRate >= 0.25) {
    alerts.push({
      id: 'fallback_rate',
      severity: 'watch',
      title: 'Elevated fallback usage',
      detail: 'Fallback model usage is rising and may indicate transcript quality drift.',
      currentValue: `${formatPercent(telemetry.fallbackRate)} fallback usage across ${chunkCount} transcript chunks.`,
      thresholdGuidance:
        'Watch at >=25% fallback usage with at least 12 chunks. Tune routing and noisy-call handling before changing thresholds.',
      runbook:
        'Review recent stream metadata and compare transcript clarity across the top affected conversations.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  if (chunkCount >= 12 && telemetry.emptyTranscriptRate >= 0.2) {
    alerts.push({
      id: 'empty_transcripts',
      severity: 'critical',
      title: 'High empty-transcript rate',
      detail:
        'A significant share of transcript chunks were persisted without usable text content.',
      currentValue: `${formatPercent(telemetry.emptyTranscriptRate)} empty transcript chunks across ${chunkCount} transcript chunks.`,
      thresholdGuidance:
        'Critical at >=20% empty transcripts with at least 12 chunks. Inspect model output integrity before relaxing thresholds.',
      runbook:
        'Inspect top attempt errors, confirm model outputs include text payloads, and review noise-heavy calls for stream quality issues.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  } else if (chunkCount >= 12 && telemetry.emptyTranscriptRate >= 0.1) {
    alerts.push({
      id: 'empty_transcripts',
      severity: 'watch',
      title: 'Elevated empty-transcript rate',
      detail: 'Empty transcript chunks are trending up and may reduce downstream insight quality.',
      currentValue: `${formatPercent(telemetry.emptyTranscriptRate)} empty transcript chunks across ${chunkCount} transcript chunks.`,
      thresholdGuidance:
        'Watch at >=10% empty transcripts with at least 12 chunks. Tune silence/noise handling before editing alert thresholds.',
      runbook:
        'Review stream-level metadata, compare primary/fallback model outputs, and tighten silence/noise handling.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  if (chunkCount >= 12 && telemetry.attemptFailureRate >= 0.3) {
    alerts.push({
      id: 'attempt_failures',
      severity: 'critical',
      title: 'Frequent transcript attempt failures',
      detail: 'Transcription attempts are failing often before a usable transcript is produced.',
      currentValue: `${formatPercent(telemetry.attemptFailureRate)} attempt failures across ${chunkCount} transcript chunks.`,
      thresholdGuidance:
        'Critical at >=30% attempt failures with at least 12 chunks. Fix provider/model stability before weakening alert sensitivity.',
      runbook:
        'Inspect Workers AI availability and retry diagnostics, then validate account limits and model access.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  } else if (chunkCount >= 12 && telemetry.attemptFailureRate >= 0.15) {
    alerts.push({
      id: 'attempt_failures',
      severity: 'watch',
      title: 'Transcript retries increasing',
      detail: 'Retry/failure pressure is above normal and could increase latency.',
      currentValue: `${formatPercent(telemetry.attemptFailureRate)} attempt failures across ${chunkCount} transcript chunks.`,
      thresholdGuidance:
        'Watch at >=15% attempt failures with at least 12 chunks. Tune stream quality or model routing before changing thresholds.',
      runbook:
        'Audit noisy calls and packet quality, and confirm stream chunk sizes are within expected bounds.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  if (chunkCount >= 12 && telemetry.streamAttemptFailureRate >= 0.65) {
    alerts.push({
      id: 'stream_attempt_failures',
      severity: 'critical',
      title: 'Attempt failures concentrated across streams',
      detail:
        'Most recent streams are showing at least one failed transcript attempt, indicating broad transcription instability.',
      currentValue: `${formatPercent(telemetry.streamAttemptFailureRate)} of streams show at least one failed transcript attempt.`,
      thresholdGuidance:
        'Critical at >=65% stream failure coverage with at least 12 chunks. Prioritize system-wide routing or provider checks before tuning thresholds.',
      runbook:
        'Compare per-model attempt-failure rates, inspect top attempt errors, and validate Workers AI capacity for this interval.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  } else if (chunkCount >= 12 && telemetry.streamAttemptFailureRate >= 0.4) {
    alerts.push({
      id: 'stream_attempt_failures',
      severity: 'watch',
      title: 'Stream-level attempt failures rising',
      detail:
        'A growing portion of streams are experiencing retries or failed transcript attempts.',
      currentValue: `${formatPercent(telemetry.streamAttemptFailureRate)} of streams show at least one failed transcript attempt.`,
      thresholdGuidance:
        'Watch at >=40% stream failure coverage with at least 12 chunks. Tune network/noisy-call handling before loosening thresholds.',
      runbook:
        'Review noisy calls and degraded networks, and confirm transcript model fallback order is still optimal.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  if (chunkCount >= 12 && telemetry.streamEmptyTranscriptRate >= 0.5) {
    alerts.push({
      id: 'stream_empty_transcripts',
      severity: 'critical',
      title: 'Empty transcripts affecting many streams',
      detail:
        'Empty transcript chunks are distributed across a large share of streams, not isolated to single calls.',
      currentValue: `${formatPercent(telemetry.streamEmptyTranscriptRate)} of streams show empty transcript output.`,
      thresholdGuidance:
        'Critical at >=50% stream empty-transcript coverage with at least 12 chunks. Repair payload/model quality before changing thresholds.',
      runbook:
        'Inspect stream payload integrity and silence/noise thresholds, and validate model responses include text before persistence.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  } else if (chunkCount >= 12 && telemetry.streamEmptyTranscriptRate >= 0.25) {
    alerts.push({
      id: 'stream_empty_transcripts',
      severity: 'watch',
      title: 'Empty transcripts spreading across streams',
      detail: 'Multiple streams are now affected by empty transcript output.',
      currentValue: `${formatPercent(telemetry.streamEmptyTranscriptRate)} of streams show empty transcript output.`,
      thresholdGuidance:
        'Watch at >=25% stream empty-transcript coverage with at least 12 chunks. Tune stream integrity and fallback behavior before relaxing thresholds.',
      runbook:
        'Correlate affected streams with top attempt errors and check whether fallback models reduce empty output rate.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  if (
    chunkCount >= 12 &&
    (telemetry.streamDelayedGapRate >= 0.5 || telemetry.largestObservedGapSeconds >= 18)
  ) {
    alerts.push({
      id: 'delayed_chunk_gaps',
      severity: 'critical',
      title: 'Delayed transcript chunk gaps',
      detail:
        'Chunk arrival gaps are large enough to create visible transcript lag across active calls.',
      currentValue: `${formatPercent(telemetry.streamDelayedGapRate)} of streams show delayed chunk gaps. Worst observed gap: ${Math.round(telemetry.largestObservedGapSeconds)}s.`,
      thresholdGuidance:
        'Critical at >=50% delayed-gap stream coverage or any observed gap >=18s with at least 12 chunks. Fix stream latency before weakening alert thresholds.',
      runbook:
        'Inspect flush queue latency, fallback retry pressure, and live polling cadence for the worst affected streams.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  } else if (
    chunkCount >= 12 &&
    (telemetry.streamDelayedGapRate >= 0.25 || telemetry.largestObservedGapSeconds >= 10)
  ) {
    alerts.push({
      id: 'delayed_chunk_gaps',
      severity: 'watch',
      title: 'Delayed transcript chunk gaps',
      detail:
        'Transcript chunks are arriving with wider-than-expected gaps on multiple recent streams.',
      currentValue: `${formatPercent(telemetry.streamDelayedGapRate)} of streams show delayed chunk gaps. Worst observed gap: ${Math.round(telemetry.largestObservedGapSeconds)}s.`,
      thresholdGuidance:
        'Watch at >=25% delayed-gap stream coverage or any observed gap >=10s with at least 12 chunks. Tune latency before reducing alert sensitivity.',
      runbook:
        'Review the top delayed streams, correlate them with retries/fallbacks, and check active-call polling cadence.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  if (chunkCount >= 12 && telemetry.trend.previousWindowChunks > 0) {
    const worseningSignals = [
      telemetry.trend.fallbackRateDelta >= 0.08,
      telemetry.trend.attemptFailureRateDelta >= 0.08,
      telemetry.trend.emptyTranscriptRateDelta >= 0.05,
      telemetry.trend.streamAttemptFailureRateDelta >= 0.15,
      telemetry.trend.streamDelayedGapRateDelta >= 0.15,
      telemetry.trend.streamEmptyTranscriptRateDelta >= 0.1,
      telemetry.trend.stopChunkRateDelta <= -0.1,
    ].filter(Boolean).length;

    if (
      telemetry.trend.fallbackRateDelta >= 0.15 ||
      telemetry.trend.attemptFailureRateDelta >= 0.15 ||
      telemetry.trend.emptyTranscriptRateDelta >= 0.1 ||
      telemetry.trend.streamAttemptFailureRateDelta >= 0.3 ||
      telemetry.trend.streamDelayedGapRateDelta >= 0.3 ||
      telemetry.trend.streamEmptyTranscriptRateDelta >= 0.2 ||
      telemetry.trend.stopChunkRateDelta <= -0.2
    ) {
      alerts.push({
        id: 'worsening_trend',
        severity: 'critical',
        title: 'Realtime transcript quality regressing',
        detail:
          'Compared with the previous lookback window, reliability indicators have worsened across key transcript quality metrics.',
        currentValue: `Trend deltas worsened across multiple signals in the current ${telemetry.lookbackDays}-day window.`,
        thresholdGuidance:
          'Critical when one major delta or several minor deltas regress at once. Stabilize routing/finalization before softening thresholds.',
        runbook:
          'Review trend deltas, correlate with deploy/provider incidents, and tune model fallback + stream finalization handling before quality drift compounds.',
        runbookHref: '#realtime-telemetry-playbook',
        runbookLabel: 'Open transcript runbook',
      });
    } else if (worseningSignals >= 2) {
      alerts.push({
        id: 'worsening_trend',
        severity: 'watch',
        title: 'Realtime quality trend is slipping',
        detail:
          'Current telemetry is weaker than the previous window across multiple quality indicators.',
        currentValue: `Trend deltas regressed across ${worseningSignals} monitored signals in the current ${telemetry.lookbackDays}-day window.`,
        thresholdGuidance:
          'Watch when at least two quality deltas weaken together. Tune upstream quality controls before reducing alert sensitivity.',
        runbook:
          'Track trend deltas through the next window and preemptively tune noisy-call handling and transcript model routing.',
        runbookHref: '#realtime-telemetry-playbook',
        runbookLabel: 'Open transcript runbook',
      });
    }
  }

  if (chunkCount >= 12 && telemetry.staleUnfinalizedStreams >= 4) {
    alerts.push({
      id: 'stale_unfinalized_streams',
      severity: 'critical',
      title: 'Stale unfinalized realtime streams',
      detail:
        'Several unfinalized streams have gone quiet for over two minutes, which can leave transcript tails incomplete.',
      currentValue: `${telemetry.staleUnfinalizedStreams} stale unfinalized streams detected in the current window.`,
      thresholdGuidance:
        'Critical at >=4 stale unfinalized streams with at least 12 chunks. Fix close/flush handling before adjusting thresholds.',
      runbook:
        'Inspect websocket close handling and Twilio stop delivery for affected streams, then verify flush queue health.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  } else if (chunkCount >= 12 && telemetry.staleUnfinalizedStreams >= 2) {
    alerts.push({
      id: 'stale_unfinalized_streams',
      severity: 'watch',
      title: 'Unfinalized streams aging out',
      detail:
        'Some unfinalized streams have not emitted chunks recently and may indicate partial stream shutdown gaps.',
      currentValue: `${telemetry.staleUnfinalizedStreams} stale unfinalized streams detected in the current window.`,
      thresholdGuidance:
        'Watch at >=2 stale unfinalized streams with at least 12 chunks. Validate close-path recovery before loosening thresholds.',
      runbook:
        'Review top unfinalized streams and correlate with connection instability or delayed stop events.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  if (chunkCount >= 12 && telemetry.streamsWithoutStopChunk >= 4 && telemetry.stopChunkRate < 0.5) {
    alerts.push({
      id: 'stream_finalize',
      severity: 'critical',
      title: 'Frequent stream-finalization gaps',
      detail:
        'Many active streams did not emit stop-finalized transcript chunks, which risks missing trailing speech.',
      currentValue: `${telemetry.streamsWithoutStopChunk} streams missed stop-finalized chunks with ${formatPercent(telemetry.stopChunkRate)} stop coverage.`,
      thresholdGuidance:
        'Critical when >=4 streams miss stop-finalized chunks and stop coverage falls below 50%. Repair stop handling before editing thresholds.',
      runbook:
        'Confirm Twilio stream stop events are received and websocket close handling flushes remaining audio.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  } else if (
    chunkCount >= 12 &&
    telemetry.streamsWithoutStopChunk >= 2 &&
    telemetry.stopChunkRate < 0.75
  ) {
    alerts.push({
      id: 'stream_finalize',
      severity: 'watch',
      title: 'Stream finalization coverage dropping',
      detail: 'A growing subset of streams are missing stop-finalized transcript chunks.',
      currentValue: `${telemetry.streamsWithoutStopChunk} streams missed stop-finalized chunks with ${formatPercent(telemetry.stopChunkRate)} stop coverage.`,
      thresholdGuidance:
        'Watch when >=2 streams miss stop-finalized chunks and stop coverage falls below 75%. Stabilize finalize paths before reducing alert sensitivity.',
      runbook:
        'Audit recent stream closures, verify stop callbacks, and ensure flush-on-close paths stay healthy.',
      runbookHref: '#realtime-telemetry-playbook',
      runbookLabel: 'Open transcript runbook',
    });
  }

  return alerts;
}

export function resolveRealtimeTelemetryHealth(alerts: RealtimeTelemetryAlert[]): {
  severity: 'healthy' | 'watch' | 'critical';
  label: 'Healthy' | 'Watch' | 'Critical';
} {
  if (alerts.some((alert) => alert.severity === 'critical')) {
    return { severity: 'critical', label: 'Critical' };
  }

  if (alerts.length > 0) {
    return { severity: 'watch', label: 'Watch' };
  }

  return { severity: 'healthy', label: 'Healthy' };
}
